// src/pos-app/surfaces/kds/BoardSurface.js
//
// F12a-N Task K1 — the native KDS ticket board. Mounted by KdsStation when
// SURFACES.kds === 'native' (surfaces/config.js), replacing the wrapped
// legacy OrderViewPage + its station-local <Routes>/param plumbing.
//
// Contracts:
//   Visual   — docs/mockups/f12a/kds-station.html (dark-first solid tickets,
//              state strip, big timers, full-width action bar) — expressed
//              with the app's theme tokens so both themes work.
//   Function — docs/superpowers/specs/2026-07-15-f12a-n-appendix-waiter-kds-
//              inventory.md SURFACE B. PARITY GOVERNS over the mockup:
//              Done + 3s undo (NOT the mockup's Start/Bump/recall strip),
//              three tabs (active/completed/voided) with live counts,
//              sounds kept. Engine: ./lib/boardFlow.js — a faithful
//              extraction from pages/pos/OrderViewPage.js (source-range
//              table in that file's header); the legacy page is untouched.
//
// Category: arrives as a PROP — KdsStation maps config.kdsQueue
// ('kitchen'|'bar') to the tenant category name ('Kitchen'|'Bar') via its
// existing QUEUE_CATEGORY map and mounts this surface with key={category},
// so a queue switch REMOUNTS a fresh board (same semantics as the legacy
// route-param remount; no cross-queue state worth preserving). No
// useParams(), no react-router anywhere in this file — the surface never
// navigates.
//
// Deliberately NOT carried over from the legacy page (all shell-owned or
// dead weight in the native app):
//   - Sign Out button + EN/AR language dropdown (1046-1071, 959-1010): the
//     station shell owns chrome; a KDS is an unmanned wall display with no
//     operator to sign out and language comes from station setup.
//   - useBusinessDayGuard (507) + the no-tenant <Navigate> (903-905):
//     KdsStation's useBusinessDayGate keeps this surface unmounted while
//     the day is closed (DayClosedScreen instead), and enrollment binds the
//     tenant before any station renders — there is nothing to redirect.
//   - Screen Wake Lock (834-854): the native app holds a GLOBAL keep-awake
//     from boot (platform/nativeChrome.js initNativeChrome), so a
//     per-surface navigator.wakeLock request would be redundant.
//   - Notification.requestPermission (828-832): vestigial in legacy — no
//     `new Notification()` call exists anywhere in that file (inventory
//     Surface B flags it); alerts are the sound chain below.
//   - The "Live" pulse dot (1046-1054): the shell's online/reconnecting
//     pill already communicates connectivity next to this board.
//
// Timer lifecycle (inventory's top hazard — THREE concurrent systems):
//   1. 5s fetch poll        — setInterval, cleared by its effect cleanup.
//   2. 1s display tick      — setInterval, cleared by its effect cleanup.
//   3. 3s undo soft-commits — one setTimeout PER completing ticket, held in
//      the undoTimeouts ref map; each is cleared on Undo, deleted on
//      commit, and the whole map is swept on unmount.
//   Plus: the 2.5s new-order pulse reset (a setTimeout legacy leaks on
//   unmount — here it is held in pulseTimeoutRef, replaced on every new
//   pulse and swept on unmount), the visibilitychange + resize listeners,
//   and the once-only audio warm-up gesture listeners — every one removed
//   in its effect's cleanup.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage, translateDynamic, getTransliteration } from '../../../context/LanguageContext';
import { orderTrackingService, categoryService, voidService } from '../../../services/api';
import { renderEntityImage } from '../../../utils/imageUtils';
import dedupeByName from '../../../utils/dedupeByName';
import tenantStorage from '../../../services/tenantStorage';
// NOTE: lib/boardFlow.js's normalizeUtc (deliberate append-Z UTC exception —
// see its header) backs getElapsedSeconds below; it is not imported directly.
import {
    getElapsedSeconds,
    formatTimer,
    getUrgency,
    getGridColumns,
    usesCarousel,
    isShortViewport,
    resolveThresholds,
    diffNewActiveIds,
    emitOrderCompletedEvent,
    buildVoidsQuery,
    filterVoids,
    getPrepMinutes,
    computeCompletedStats,
    playNotificationSound,
} from './lib/boardFlow';

const CSS = `
.kbs-root{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;
  background:var(--c-bg);color:var(--c-text);font-family:inherit;}

/* ---- toolbar: tabs + sound controls ---- */
.kbs-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px;flex:none;
  padding:10px 14px;border-bottom:1px solid var(--c-border);background:var(--c-surface);flex-wrap:wrap;}
.kbs-tabs{display:flex;gap:4px;}
.kbs-tab{display:inline-flex;align-items:center;gap:6px;min-height:44px;padding:0 16px;
  border:none;border-radius:10px;background:transparent;color:var(--c-muted);cursor:pointer;
  font-family:inherit;font-size:13px;font-weight:800;transition:background .15s ease,color .15s ease;}
.kbs-tab.on{background:var(--c-blueLight);color:var(--c-blue);}
.kbs-tab.kbs-tab-voided.on{background:var(--c-redLight);color:var(--c-red);}
.kbs-tab-count{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;
  padding:0 6px;border-radius:10px;font-size:11px;font-weight:800;font-variant-numeric:tabular-nums;
  background:var(--c-faint);border:1px solid var(--c-border);color:inherit;}
.kbs-tools{display:flex;align-items:center;gap:8px;}
.kbs-tool{width:44px;height:44px;border-radius:10px;display:flex;align-items:center;justify-content:center;
  background:transparent;border:1px solid var(--c-border);color:var(--c-muted);cursor:pointer;
  transition:background .15s ease,color .15s ease;}
.kbs-tool.on{color:var(--c-success);border-color:color-mix(in srgb, var(--c-success) 45%, transparent);}
.kbs-tool:active{background:var(--c-faint);}

/* ---- banners ---- */
.kbs-unlock{display:flex;align-items:center;justify-content:center;gap:8px;flex:none;
  margin:10px 14px 0;padding:10px 16px;border-radius:10px;cursor:pointer;
  background:var(--c-amberLight);border:1px solid var(--c-amberBorder);color:var(--c-amber);
  font-family:inherit;font-size:13px;font-weight:700;}
.kbs-salesdate{display:flex;align-items:center;justify-content:center;gap:8px;flex:none;
  margin:10px 14px 0;padding:7px 16px;border-radius:10px;font-size:13px;font-weight:700;}
.kbs-salesdate .kbs-sd-dot{width:8px;height:8px;border-radius:50%;flex:none;}
.kbs-salesdate.kbs-sd-on{background:var(--c-blueLight);border:1px solid var(--c-blueBorder);color:var(--c-blue);}
.kbs-salesdate.kbs-sd-on .kbs-sd-dot{background:var(--c-blue);}
.kbs-salesdate.kbs-sd-off{background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-red);}
.kbs-salesdate.kbs-sd-off .kbs-sd-dot{background:var(--c-red);}
.kbs-sd-value{font-variant-numeric:tabular-nums;font-weight:600;color:var(--c-slate);}

/* ---- body ---- */
.kbs-body{flex:1;min-height:0;padding:12px 14px;display:flex;flex-direction:column;overflow:hidden;}
.kbs-scroll{flex:1;min-height:0;overflow-y:auto;}
.kbs-center{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;
  color:var(--c-muted);}
.kbs-spinner{width:40px;height:40px;border-radius:50%;border:3px solid var(--c-border);
  border-top-color:var(--c-green);animation:kbsSpin .8s linear infinite;}
.kbs-empty-title{font-size:18px;font-weight:800;color:var(--c-slate);}
.kbs-empty-sub{font-size:13px;color:var(--c-muted);}
.kbs-empty-art{font-size:56px;line-height:1;}

/* ---- active grid / carousel ---- */
.kbs-grid{display:grid;gap:12px;align-content:start;overflow-y:auto;flex:1;min-height:0;
  /* A card must be as tall as its contents, never as tall as the row it
     happens to share. Without these two, a short landscape viewport made
     every row split the available height and .kbs-ticket's overflow:hidden
     cut the items and the Done button clean off. */
  grid-auto-rows:max-content;align-items:start;
  /* Landscape puts the notch on one side; without this the last column
     disappears under it. */
  padding-inline:max(0px, env(safe-area-inset-left)) max(0px, env(safe-area-inset-right));}
.kbs-carousel{display:flex;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;
  -webkit-overflow-scrolling:touch;flex:1;min-height:0;scrollbar-width:none;}
.kbs-carousel::-webkit-scrollbar{display:none;}
.kbs-page{scroll-snap-align:start;flex:none;width:100%;display:flex;flex-direction:column;gap:10px;
  padding:0 4px;justify-content:flex-start;overflow-y:auto;}
.kbs-dots{display:flex;justify-content:center;gap:6px;padding:10px 0 2px;flex:none;}
.kbs-dot{width:8px;height:8px;border-radius:50%;background:var(--c-border);transition:background .2s;}
.kbs-dot.on{background:var(--c-blue);}

/* ---- ticket (mockup language: strip + big timer + solid card) ---- */
.kbs-ticket{background:var(--c-surface);border:1px solid var(--c-border);
  border-inline-start-width:6px;border-radius:14px;overflow:hidden;display:flex;flex-direction:column;
  box-shadow:0 4px 14px color-mix(in srgb, var(--c-ink) 8%, transparent);}
.kbs-ticket[data-state="fresh"]{border-inline-start-color:var(--c-success);}
.kbs-ticket[data-state="warning"]{border-inline-start-color:var(--c-amber);}
.kbs-ticket[data-state="critical"]{border-inline-start-color:var(--c-red);}
.kbs-ticket.kbs-new{animation:kbsEnter .3s ease, kbsGlow 2s ease;}
.kbs-ticket.kbs-completing{opacity:.85;}
.kbs-strip{display:flex;align-items:center;justify-content:space-between;padding:6px 13px;
  font-size:11px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;}
.kbs-strip .kbs-state{display:inline-flex;align-items:center;gap:6px;}
.kbs-strip .kbs-strip-count{font-variant-numeric:tabular-nums;}
.kbs-ticket[data-state="fresh"] .kbs-strip{background:color-mix(in srgb, var(--c-success) 12%, transparent);color:var(--c-success);}
.kbs-ticket[data-state="warning"] .kbs-strip{background:color-mix(in srgb, var(--c-amber) 12%, transparent);color:var(--c-amber);}
.kbs-ticket[data-state="critical"] .kbs-strip{background:color-mix(in srgb, var(--c-red) 14%, transparent);color:var(--c-red);}
.kbs-ticket.kbs-completing .kbs-strip{background:color-mix(in srgb, var(--c-success) 12%, transparent);color:var(--c-success);}
@media (prefers-reduced-motion: no-preference){
  .kbs-ticket[data-state="critical"]:not(.kbs-completing){animation:kbsOverduePulse 1.6s ease-in-out infinite;}
}
.kbs-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;padding:10px 13px 2px;}
.kbs-ref{font-size:20px;font-weight:800;letter-spacing:-0.01em;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
.kbs-edited{background:var(--c-amberLight);color:var(--c-amber);border:1px solid var(--c-amberBorder);
  padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:normal;}
.kbs-meta{font-size:10.5px;color:var(--c-mutedLight);margin-top:2px;overflow-wrap:anywhere;}
.kbs-meta strong{color:var(--c-muted);font-weight:700;}
.kbs-timer{font-size:28px;font-weight:800;line-height:1;font-variant-numeric:tabular-nums;flex:none;}
.kbs-ticket[data-state="fresh"] .kbs-timer{color:var(--c-success);}
.kbs-ticket[data-state="warning"] .kbs-timer{color:var(--c-amber);}
.kbs-ticket[data-state="critical"] .kbs-timer{color:var(--c-red);}
.kbs-items{padding:7px 13px 12px;display:flex;flex-direction:column;gap:8px;
  border-top:1px solid var(--c-border);margin-top:6px;transition:opacity .3s;}
.kbs-completing .kbs-items,.kbs-completing .kbs-head{opacity:.4;}
.kbs-item-main{display:flex;gap:9px;align-items:baseline;font-size:15px;font-weight:800;line-height:1.25;min-width:0;}
.kbs-item-qty{color:var(--c-blue);min-width:26px;flex:none;font-variant-numeric:tabular-nums;}
.kbs-item-name{overflow-wrap:anywhere;min-width:0;}
.kbs-addons{margin-inline-start:35px;margin-top:3px;display:flex;flex-wrap:wrap;gap:3px;}
.kbs-addon{color:var(--c-purple);font-size:11px;background:var(--c-purpleLight);padding:2px 6px;border-radius:4px;}
.kbs-addon-d1{margin-inline-start:12px;opacity:.85;}
.kbs-addon-d2{margin-inline-start:24px;opacity:.75;}
.kbs-item-note{margin-inline-start:35px;margin-top:3px;color:var(--c-amber);font-size:12px;font-style:italic;font-weight:600;}
.kbs-order-note{margin-top:4px;background:var(--c-amberLight);border:1px solid var(--c-amberBorder);
  color:var(--c-amber);padding:6px 8px;border-radius:8px;font-size:12px;font-weight:600;}
.kbs-done{display:flex;align-items:center;justify-content:center;gap:9px;width:100%;min-height:50px;
  font-family:inherit;font-size:15px;font-weight:800;letter-spacing:.03em;cursor:pointer;
  border:none;border-top:1px solid var(--c-border);background:var(--c-success);color:#fff;
  transition:filter .12s ease;}
.kbs-done:active{filter:brightness(1.15);}
.kbs-ticket[data-state="critical"] .kbs-done{background:var(--c-red);}

/* tight — a landscape phone: four ~190px columns in 393px of height.
   Not the same thing as the compact class (a wall board running 5-6 columns),
   which is why it is separate: a 55-inch display has room for the meta line,
   a phone does not.

   Three changes, in priority order (content-priority: core first, secondary
   folded):
     1. .kbs-meta is hidden. "Jane Doe · TRK8F6F6970 · CS-20260921-001" wrapped
        to five lines and ate the card. A chef matches a ticket by its order
        ref and its items; the tracking and session ids are for support, and
        they are one rotation away.
     2. .kbs-head stacks. Side-by-side, a wrapping ref ran underneath a
        flex:none timer and the two overlapped. A column cannot collide.
     3. Type steps down to suit the measure, and the Done button holds 44pt —
        it is the one thing on this card that gets tapped, often with a wet
        or gloved hand. */
.kbs-ticket.kbs-tight .kbs-head{flex-direction:column;align-items:stretch;gap:1px;padding:8px 11px 4px;}
.kbs-ticket.kbs-tight .kbs-ref{font-size:15px;line-height:1.2;}
.kbs-ticket.kbs-tight .kbs-timer{font-size:22px;align-self:flex-start;}
.kbs-ticket.kbs-tight .kbs-meta{display:none;}
.kbs-ticket.kbs-tight .kbs-strip{padding:5px 11px;font-size:10px;}
.kbs-ticket.kbs-tight .kbs-items{padding:5px 11px 9px;gap:5px;margin-top:4px;}
.kbs-ticket.kbs-tight .kbs-item-main{font-size:13px;}
.kbs-ticket.kbs-tight .kbs-done{min-height:44px;font-size:14px;}

/* compact (grid >= 5 cols) */
.kbs-ticket.kbs-compact .kbs-head{padding:8px 11px 2px;}
.kbs-ticket.kbs-compact .kbs-ref{font-size:17px;}
.kbs-ticket.kbs-compact .kbs-timer{font-size:23px;}
.kbs-ticket.kbs-compact .kbs-items{padding:5px 11px 10px;gap:6px;}
.kbs-ticket.kbs-compact .kbs-item-main{font-size:13px;}
.kbs-ticket.kbs-compact .kbs-done{min-height:46px;font-size:14px;}

/* ---- undo bar (3s soft-commit) ---- */
.kbs-undo{padding:8px 13px 12px;border-top:1px solid var(--c-border);}
.kbs-undo-row{display:flex;align-items:center;justify-content:space-between;gap:12px;}
.kbs-undo-label{display:flex;align-items:center;gap:8px;color:var(--c-success);font-size:13px;font-weight:700;}
.kbs-undo-check{width:24px;height:24px;border-radius:50%;background:var(--c-success);color:#fff;
  display:flex;align-items:center;justify-content:center;flex:none;}
.kbs-undo-btn{background:none;border:1px solid color-mix(in srgb, var(--c-amber) 55%, transparent);
  color:var(--c-amber);padding:6px 16px;border-radius:8px;font-family:inherit;
  font-weight:800;font-size:13px;cursor:pointer;flex:none;min-height:36px;}
.kbs-undo-track{height:3px;background:color-mix(in srgb, var(--c-success) 30%, transparent);
  border-radius:2px;margin-top:8px;overflow:hidden;}
.kbs-undo-fill{height:100%;background:var(--c-success);border-radius:2px;animation:kbsUndoBar 3s linear forwards;}

/* ---- completed / voided tabs ---- */
.kbs-stats{display:grid;gap:12px;margin-bottom:14px;padding:12px 16px;flex:none;
  background:var(--c-surface);border:1px solid var(--c-border);border-radius:12px;}
.kbs-stat{text-align:center;}
.kbs-stat-value{font-size:22px;font-weight:800;color:var(--c-success);font-variant-numeric:tabular-nums;}
.kbs-stat-value.kbs-red{color:var(--c-red);}
.kbs-stat-label{font-size:10.5px;color:var(--c-mutedLight);text-transform:uppercase;letter-spacing:.5px;font-weight:700;}
.kbs-hist-grid{display:grid;gap:10px;}
.kbs-hist-card{background:var(--c-surface);border:1px solid var(--c-border);border-radius:12px;
  border-inline-start:4px solid var(--c-success);padding:12px;}
.kbs-hist-card.kbs-hist-done{opacity:.7;}
.kbs-hist-card.kbs-hist-void{border-inline-start-color:var(--c-red);position:relative;overflow:hidden;}
.kbs-hist-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;gap:8px;}
.kbs-hist-ref{font-weight:700;font-size:13px;color:var(--c-slate);}
.kbs-hist-void .kbs-hist-ref{color:var(--c-red);}
.kbs-hist-time{font-size:11px;color:var(--c-mutedLight);font-variant-numeric:tabular-nums;}
.kbs-hist-id{font-size:10px;color:var(--c-mutedLight);margin-bottom:4px;overflow-wrap:anywhere;}
.kbs-hist-items{font-size:11px;color:var(--c-muted);line-height:1.6;overflow-wrap:anywhere;}
.kbs-hist-foot{display:flex;gap:12px;margin-top:8px;padding-top:8px;border-top:1px solid var(--c-border);
  font-size:10px;color:var(--c-mutedLight);flex-wrap:wrap;}
.kbs-hist-foot strong{color:var(--c-muted);}
.kbs-hist-foot .kbs-red{color:var(--c-red);}
.kbs-void-stamp{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-18deg);
  font-size:26px;font-weight:900;color:var(--c-red);opacity:.14;letter-spacing:4px;
  pointer-events:none;text-transform:uppercase;}
.kbs-void-reason{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:150px;}

@keyframes kbsSpin{to{transform:rotate(360deg);}}
@keyframes kbsEnter{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
@keyframes kbsGlow{0%,100%{box-shadow:0 0 8px color-mix(in srgb, var(--c-success) 25%, transparent);}
  50%{box-shadow:0 0 20px color-mix(in srgb, var(--c-success) 45%, transparent);}}
@keyframes kbsUndoBar{from{width:100%;}to{width:0%;}}
@keyframes kbsOverduePulse{0%,100%{box-shadow:0 4px 14px color-mix(in srgb, var(--c-ink) 8%, transparent);}
  50%{box-shadow:0 0 0 3px color-mix(in srgb, var(--c-red) 25%, transparent),0 4px 14px color-mix(in srgb, var(--c-ink) 8%, transparent);}}
@media (prefers-reduced-motion: reduce){
  .kbs-ticket.kbs-new,.kbs-ticket[data-state="critical"]{animation:none !important;}
}
`;

// ---- small SVG icon set (mockup language; no emoji controls) -----------
const StateIcons = {
    fresh: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><polyline points="8.5 12.5 11 15 15.5 9.5" />
        </svg>
    ),
    warning: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" />
        </svg>
    ),
    critical: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
};

const SoundOnIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
);
const SoundOffIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
    </svg>
);
const TestSoundIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
);
const CheckIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" width="14" height="14" aria-hidden="true">
        <path d="M5 13l4 4L19 7" />
    </svg>
);

// Legacy urgency->strip label (mockup's state-strip language mapped onto the
// legacy fresh/warning/critical urgency — parity keeps the thresholds, the
// mockup supplies the vocabulary).
const STATE_LABEL_KEY = {
    fresh: 'kds_on_time',
    warning: 'kds_running_late',
    critical: 'kds_overdue',
};

// ── Undo Bar (legacy 290-325: 3s auto-drain progress + Undo) ───────────
const UndoBar = ({ onUndo, t }) => (
    <div className="kbs-undo">
        <div className="kbs-undo-row">
            <div className="kbs-undo-label">
                <span className="kbs-undo-check">{CheckIcon}</span>
                {t('kds_completed_label')}
            </div>
            <button type="button" className="kbs-undo-btn" onClick={onUndo}>
                {t('kds_undo')}
            </button>
        </div>
        <div className="kbs-undo-track"><div className="kbs-undo-fill" /></div>
    </div>
);

// ── Ticket card (legacy OrderCard 328-495, restyled per mockup) ────────
const TicketCard = ({ order, thresholds, isNew, isCompleting, onDone, onUndo, language, t, compact, tight }) => {
    const elapsed = getElapsedSeconds(order.submitted_at);
    const urgency = getUrgency(Math.floor(elapsed / 60), thresholds);
    const totalQty = (order.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);

    return (
        <article
            className={`kbs-ticket${compact ? ' kbs-compact' : ''}${tight ? ' kbs-tight' : ''}${isNew ? ' kbs-new' : ''}${isCompleting ? ' kbs-completing' : ''}`}
            data-state={urgency}
        >
            <div className="kbs-strip">
                <span className="kbs-state">
                    {isCompleting ? CheckIcon : StateIcons[urgency]}
                    {isCompleting ? t('kds_completed_label') : t(STATE_LABEL_KEY[urgency])}
                </span>
                <span className="kbs-strip-count">{totalQty} {t('items')}</span>
            </div>

            <div className="kbs-head">
                <div style={{ minWidth: 0 }}>
                    <div className="kbs-ref">
                        <span>
                            {translateDynamic(order.table_info?.dining_option || 'unknown', t)}{' '}
                            #{order.table_info?.table_number || order.order_id || order.tracking_id?.slice(-4).toUpperCase() || '?'}
                        </span>
                        {order.is_edited && <span className="kbs-edited">{t('updated_order')}</span>}
                    </div>
                    <div className="kbs-meta">
                        {order.table_info?.staff_name && <strong>{order.table_info.staff_name} &middot; </strong>}
                        {order.tracking_id}
                        {order.session_id && <span> &middot; {order.session_id}</span>}
                    </div>
                </div>
                <span className="kbs-timer">{formatTimer(elapsed)}</span>
            </div>

            <div className="kbs-items">
                {order.items?.map((item, idx) => (
                    <div key={idx}>
                        <div className="kbs-item-main">
                            <span className="kbs-item-qty">{item.quantity}&times;</span>
                            <span className="kbs-item-name">{getTransliteration(item.name, language, t)}</span>
                        </div>
                        {item.add_ons?.length > 0 && (
                            <div className="kbs-addons">
                                {item.add_ons.map((addon, ai) => (
                                    <span
                                        key={ai}
                                        className={`kbs-addon${addon.depth ? ` kbs-addon-d${Math.min(addon.depth, 2)}` : ''}`}
                                    >
                                        {addon.depth ? '↳' : '+'} {addon.name || addon}
                                    </span>
                                ))}
                            </div>
                        )}
                        {item.notes && <div className="kbs-item-note">{item.notes}</div>}
                    </div>
                ))}
                {order.notes && (
                    <div className="kbs-order-note">
                        <span style={{ fontWeight: 800 }}>{t('kds_note')}:</span> {order.notes}
                    </div>
                )}
            </div>

            {isCompleting ? (
                <UndoBar onUndo={onUndo} t={t} />
            ) : (
                <button type="button" className="kbs-done" onClick={onDone}>
                    {CheckIcon}
                    {t('kds_done')}
                </button>
            )}
        </article>
    );
};

// ── Completed tab (legacy CompletedTab 97-195) ─────────────────────────
const CompletedTab = ({ orders, thresholds, t, language, windowWidth }) => {
    const stats = computeCompletedStats(orders, thresholds);
    const strip = [
        { value: stats.completedCount, label: t('kds_completed_today') },
        { value: `${stats.avgPrep}m`, label: t('kds_avg_prep_time') },
        { value: stats.totalItems, label: t('kds_items_prepared') },
        { value: stats.overThreshold, label: t('kds_over_threshold') },
    ];
    const narrow = windowWidth < 768;

    return (
        <div className="kbs-scroll">
            <div className="kbs-stats" style={{ gridTemplateColumns: narrow ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)' }}>
                {strip.map((s, i) => (
                    <div key={i} className="kbs-stat">
                        <div className="kbs-stat-value">{s.value}</div>
                        <div className="kbs-stat-label">{s.label}</div>
                    </div>
                ))}
            </div>
            {orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--c-mutedLight)' }}>
                    {t('kds_no_active_orders')}
                </div>
            ) : (
                <div className="kbs-hist-grid" style={{ gridTemplateColumns: narrow ? '1fr' : 'repeat(4, 1fr)' }}>
                    {orders.map(order => {
                        const prepTime = getPrepMinutes(order);
                        const itemSummary = (order.items || [])
                            .map(i => `${i.quantity}x ${getTransliteration(i.name, language, t)}`)
                            .join(', ');
                        return (
                            <div key={order.tracking_id} className="kbs-hist-card kbs-hist-done">
                                <div className="kbs-hist-top">
                                    <span className="kbs-hist-ref">
                                        {translateDynamic(order.table_info?.dining_option || '', t)}{' '}
                                        #{order.table_info?.table_number || order.order_id || '?'}
                                    </span>
                                    <span className="kbs-hist-time">
                                        {order.completed_at ? format(parseISO(order.completed_at), 'HH:mm') : ''}
                                    </span>
                                </div>
                                <div className="kbs-hist-id">{order.tracking_id}</div>
                                <div className="kbs-hist-items">{itemSummary}</div>
                                <div className="kbs-hist-foot">
                                    {prepTime !== null && (
                                        <span>{t('kds_prep_time')}: <strong>{prepTime}m</strong></span>
                                    )}
                                    <span>{t('kds_items')}: <strong>{order.total_items_count}</strong></span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Voided tab (legacy VoidedTab 198-287) ──────────────────────────────
const VoidedTab = ({ voidedOrders, t, language, windowWidth }) => {
    const narrow = windowWidth < 768;
    const itemsVoided = voidedOrders.reduce(
        (sum, v) => sum + ((v.record_data?.items || []).length || v.record_data?.total_items_count || 0), 0
    );

    return (
        <div className="kbs-scroll">
            <div className="kbs-stats" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="kbs-stat">
                    <div className="kbs-stat-value kbs-red">{voidedOrders.length}</div>
                    <div className="kbs-stat-label">{t('kds_voided_today')}</div>
                </div>
                <div className="kbs-stat">
                    <div className="kbs-stat-value kbs-red">{itemsVoided}</div>
                    <div className="kbs-stat-label">{t('kds_items_voided')}</div>
                </div>
            </div>
            {voidedOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--c-mutedLight)' }}>
                    {t('kds_no_voided_orders')}
                </div>
            ) : (
                <div className="kbs-hist-grid" style={{ gridTemplateColumns: narrow ? '1fr' : 'repeat(4, 1fr)' }}>
                    {voidedOrders.map(v => {
                        const rd = v.record_data || {};
                        const items = rd.items || [];
                        const tableInfo = rd.table_info || {};
                        const itemSummary = items.length > 0
                            ? items.map(i => `${i.quantity || 1}x ${getTransliteration(i.name || i.product_name || 'Item', language, t)}`).join(', ')
                            : `${rd.total_items_count || 0} ${t('items')}`;
                        return (
                            <div key={v.void_id} className="kbs-hist-card kbs-hist-void">
                                <div className="kbs-void-stamp">{t('voided')}</div>
                                <div className="kbs-hist-top">
                                    <span className="kbs-hist-ref">
                                        {translateDynamic(tableInfo.dining_option || rd.category || '', t)}{' '}
                                        #{tableInfo.table_number || rd.order_id || '?'}
                                    </span>
                                    <span className="kbs-hist-time">
                                        {v.deleted_at ? format(new Date(v.deleted_at), 'HH:mm') : ''}
                                    </span>
                                </div>
                                <div className="kbs-hist-id">{rd.tracking_id || v.record_id}</div>
                                <div className="kbs-hist-items">{itemSummary}</div>
                                <div className="kbs-hist-foot">
                                    <span>{t('voids_deleted_by')}: <strong className="kbs-red">{v.authorized_by}</strong></span>
                                    {v.reason && <span className="kbs-void-reason">{v.reason}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Main surface ───────────────────────────────────────────────────────
export default function BoardSurface({ category }) {
    const { getTenantSetting } = useAuth();
    const { t, language } = useLanguage();

    // ---- state (legacy 519-534, minus the shell-owned pieces) ----------
    const [orders, setOrders] = useState([]);
    const [completedOrders, setCompletedOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed' | 'voided'
    const [voidedOrdersList, setVoidedOrdersList] = useState([]);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('kds_sound_enabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [completingOrders, setCompletingOrders] = useState({}); // trackingId → true
    const [tick, setTick] = useState(0); // eslint-disable-line no-unused-vars -- forces the 1s timer re-render, same as legacy 530
    const [newOrderIds, setNewOrderIds] = useState(new Set());
    const [categoryImageUrl, setCategoryImageUrl] = useState(null);
    const [activeSalesDate, setActiveSalesDate] = useState(null);
    const [audioUnlocked, setAudioUnlocked] = useState(false);

    const undoTimeouts = useRef({});
    const pulseTimeoutRef = useRef(null); // legacy leaks this one (616) — tracked here
    const playSoundRef = useRef(null);
    const previousOrderIds = useRef(new Set());
    const initialFetchDone = useRef(false);
    const audioContextRef = useRef(null);

    // ---- sales date (legacy 536-546) -----------------------------------
    useEffect(() => {
        const loadSalesDate = async () => {
            try {
                const state = await tenantStorage.getBusinessDayState();
                setActiveSalesDate(state.isBusinessDay ? state.salesDate : null);
            } catch (e) {
                console.error('Failed to load sales date:', e);
            }
        };
        loadSalesDate();
    }, []);

    // ---- category image (legacy 548-553) -------------------------------
    useEffect(() => {
        categoryService.getAll().then(cats => {
            const cat = dedupeByName(cats).find(c => c.name === category);
            if (cat?.image_url) setCategoryImageUrl(cat.image_url);
        }).catch(() => {});
    }, [category]);

    // ---- thresholds (legacy 561-565 via lib) ---------------------------
    const thresholds = useMemo(
        () => resolveThresholds(getTenantSetting, category),
        [category, getTenantSetting]
    );

    // ---- derived lists (legacy 567-578) --------------------------------
    const activeOrders = useMemo(
        () => orders.filter(o => o.status !== 'completed' && !completingOrders[o.tracking_id]),
        [orders, completingOrders]
    );
    const completingOrdersList = useMemo(
        () => orders.filter(o => completingOrders[o.tracking_id]),
        [orders, completingOrders]
    );
    const visibleOrders = useMemo(
        () => [...activeOrders, ...completingOrdersList],
        [activeOrders, completingOrdersList]
    );

    // ---- responsive (legacy 580-601) -----------------------------------
    const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
    // Height matters as much as width here: a phone held sideways is wide and
    // short, and a column needs vertical room to be readable. See
    // getGridColumns for the Creamio report this comes from.
    const [windowHeight, setWindowHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 768));
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setWindowHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        // An orientation change fires resize on every browser the POS runs in,
        // so no separate orientationchange listener is needed.
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const gridCols = useMemo(
        () => getGridColumns(visibleOrders.length, windowWidth, windowHeight),
        [visibleOrders.length, windowWidth, windowHeight]
    );
    // Only a PORTRAIT phone takes the carousel. A landscape phone is short
    // but wide, and the carousel's column-flex page would shrink its tickets
    // to slivers; the grid gives them natural height and scrolls vertically.
    // See usesCarousel.
    const isMobile = usesCarousel(windowWidth);
    // A landscape phone: wide enough for a grid, too short to spend pixels on
    // status. Drives both the hidden banners below and the station bar above
    // (KdsStation listens for the same signal).
    const compactChrome = isShortViewport(windowHeight);

    const carouselRef = useRef(null);
    const [activeCarouselPage, setActiveCarouselPage] = useState(0);
    const handleCarouselScroll = useCallback(() => {
        if (!carouselRef.current) return;
        const el = carouselRef.current;
        const totalPages = Math.ceil(visibleOrders.length / 2);
        if (totalPages <= 0) return;
        const pageWidth = el.scrollWidth / totalPages;
        const page = Math.round(el.scrollLeft / pageWidth);
        setActiveCarouselPage(page);
    }, [visibleOrders.length]);

    // ---- data fetching (legacy 604-663; diff logic via lib) ------------
    // NOTE: legacy's fetchOrders useCallback lists `completingOrders` as a
    // dep without referencing it (629) — dropped here so completions don't
    // needlessly reset the 5s poll interval. Purely mechanical.
    const fetchOrders = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const fetched = await orderTrackingService.getOrdersByCategory(category, null);
            const newOrders = fetched || [];

            const { activeIds, brandNew } = diffNewActiveIds(newOrders, previousOrderIds.current);
            if (initialFetchDone.current && brandNew.length > 0) {
                setNewOrderIds(new Set(brandNew));
                if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
                pulseTimeoutRef.current = setTimeout(() => setNewOrderIds(new Set()), 2500);
                if (playSoundRef.current) playSoundRef.current();
            }
            initialFetchDone.current = true;
            previousOrderIds.current = activeIds;

            setOrders(newOrders);
        } catch (err) {
            console.error(`Error fetching ${category} orders:`, err);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [category]);

    const fetchCompleted = useCallback(async () => {
        try {
            const fetched = await orderTrackingService.getCompletedOrdersByCategory(category);
            setCompletedOrders(fetched || []);
        } catch (err) {
            console.error('Error fetching completed orders:', err);
        }
    }, [category]);

    const fetchVoided = useCallback(async () => {
        // No active sales date = no voided orders to show (legacy 641-645)
        if (!activeSalesDate) {
            setVoidedOrdersList([]);
            return;
        }
        try {
            const data = await voidService.getVoids(buildVoidsQuery(activeSalesDate));
            setVoidedOrdersList(filterVoids(data.items, category, activeSalesDate));
        } catch (err) {
            console.error('Error fetching voided orders:', err);
        }
    }, [category, activeSalesDate]);

    // Initial fetch (legacy 665-670)
    useEffect(() => {
        fetchOrders(true);
        fetchCompleted();
        fetchVoided();
    }, [fetchOrders, fetchCompleted, fetchVoided]);

    // Poll every 5 seconds — primary real-time mechanism (legacy 672-680;
    // explicitly NOT event-driven: the KDS is a separate device, it polls)
    useEffect(() => {
        const interval = setInterval(() => {
            fetchOrders(false);
            if (activeTab === 'completed') fetchCompleted();
            if (activeTab === 'voided') fetchVoided();
        }, 5000);
        return () => clearInterval(interval);
    }, [fetchOrders, fetchCompleted, fetchVoided, activeTab]);

    // Catch up when the page becomes visible again (legacy 683-693)
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchOrders(false);
                if (activeTab === 'completed') fetchCompleted();
                if (activeTab === 'voided') fetchVoided();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [fetchOrders, fetchCompleted, fetchVoided, activeTab]);

    // 1s display tick for the live timers (legacy 696-699)
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    // ---- completion + undo (legacy 702-761) ----------------------------
    const handleDone = useCallback((trackingId) => {
        const timeout = setTimeout(async () => {
            // Undo window expired — commit to backend
            try {
                await orderTrackingService.updateOrderStatus(trackingId, 'completed');
                emitOrderCompletedEvent(trackingId, category);
                setOrders(prev => prev.filter(o => o.tracking_id !== trackingId));
                fetchCompleted();
            } catch (err) {
                console.error('Error completing order:', err);
            }
            setCompletingOrders(prev => {
                const next = { ...prev };
                delete next[trackingId];
                return next;
            });
            delete undoTimeouts.current[trackingId];
        }, 3000);

        undoTimeouts.current[trackingId] = timeout;
        setCompletingOrders(prev => ({ ...prev, [trackingId]: true }));
    }, [category, fetchCompleted]);

    const handleUndo = useCallback((trackingId) => {
        // Cancel the pending completion — no backend call was made yet
        if (undoTimeouts.current[trackingId]) {
            clearTimeout(undoTimeouts.current[trackingId]);
            delete undoTimeouts.current[trackingId];
        }
        setCompletingOrders(prev => {
            const next = { ...prev };
            delete next[trackingId];
            return next;
        });
    }, []);

    // Sweep every pending timeout on unmount (legacy 756-761 + the pulse
    // timeout legacy leaks — see header "Timer lifecycle")
    useEffect(() => {
        const timeouts = undoTimeouts.current;
        return () => {
            Object.values(timeouts).forEach(clearTimeout);
            if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
        };
    }, []);

    // ---- sound (legacy 763-820; 3-tier playback via lib) ---------------
    // Warm up AudioContext on first user interaction (autoplay policy)
    useEffect(() => {
        const warmUp = async () => {
            try {
                if (!audioContextRef.current) {
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                if (audioContextRef.current.state === 'suspended') {
                    await audioContextRef.current.resume();
                }
                setAudioUnlocked(true);
            } catch (e) { /* ignore */ }
        };
        const events = ['click', 'touchstart', 'keydown'];
        events.forEach(ev => document.addEventListener(ev, warmUp, { once: true }));
        return () => events.forEach(ev => document.removeEventListener(ev, warmUp));
    }, []);

    const playSound = useCallback(
        () => playNotificationSound({ soundEnabled, audioRef: audioContextRef }),
        [soundEnabled]
    );
    playSoundRef.current = playSound;

    const toggleSound = () => {
        const next = !soundEnabled;
        setSoundEnabled(next);
        localStorage.setItem('kds_sound_enabled', JSON.stringify(next));
    };

    const unlockAudio = async () => {
        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            setAudioUnlocked(true);
        } catch { /* ignore */ }
    };

    // ---- render --------------------------------------------------------
    const tabs = [
        { key: 'active', label: t('kds_active'), count: activeOrders.length },
        { key: 'completed', label: t('kds_completed'), count: completedOrders.length },
        { key: 'voided', label: t('voided'), count: voidedOrdersList.length },
    ];
    const carouselPages = Math.ceil(visibleOrders.length / 2);

    return (
        <div className="kbs-root">
            <style>{CSS}</style>

            <div className="kbs-toolbar">
                <div className="kbs-tabs" role="tablist" aria-label={t('Orders')}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`kbs-tab${tab.key === 'voided' ? ' kbs-tab-voided' : ''}${activeTab === tab.key ? ' on' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                            <span className="kbs-tab-count">{tab.count}</span>
                        </button>
                    ))}
                </div>
                <div className="kbs-tools">
                    <button
                        type="button"
                        className={`kbs-tool${soundEnabled ? ' on' : ''}`}
                        onClick={toggleSound}
                        title={soundEnabled ? t('kds_sound_on') : t('kds_sound_off')}
                        aria-label={soundEnabled ? t('kds_sound_on') : t('kds_sound_off')}
                    >
                        {soundEnabled ? SoundOnIcon : SoundOffIcon}
                    </button>
                    {soundEnabled && (
                        <button
                            type="button"
                            className="kbs-tool kbs-test-sound"
                            onClick={playSound}
                            title={t('kds_test_sound')}
                            aria-label={t('kds_test_sound')}
                        >
                            {TestSoundIcon}
                        </button>
                    )}
                </div>
            </div>

            {/* Everything between the tabs and the tickets is suppressed on a
                short viewport — a phone in landscape. The chrome above the
                board came to ~360px of a 393px screen (station bar, tabs,
                sound prompt, sales-date strip), leaving ~150px of tickets: the
                shift Creamio's kitchen gave up on. The tabs survive because
                they are how you reach Completed and Voided; the rest is
                status, and status is not worth the board.

                Nothing is lost, only hidden: the sales date is still on the
                station bar in portrait, and sound can still be enabled there
                or by rotating back. */}
            {!compactChrome && soundEnabled && !audioUnlocked && (
                <button type="button" className="kbs-unlock" onClick={unlockAudio}>
                    {SoundOnIcon} {t('kds_tap_to_enable_sound')}
                </button>
            )}

            {!compactChrome && (activeSalesDate ? (
                <div className="kbs-salesdate kbs-sd-on">
                    <span className="kbs-sd-dot" aria-hidden="true" />
                    <span>{t('sales_date')}</span>
                    <span className="kbs-sd-value">{activeSalesDate}</span>
                </div>
            ) : (
                <div className="kbs-salesdate kbs-sd-off">
                    <span className="kbs-sd-dot" aria-hidden="true" />
                    <span>{t('no_sales_date')}</span>
                </div>
            ))}

            <div className="kbs-body">
                {loading ? (
                    <div className="kbs-center">
                        <div className="kbs-spinner" aria-hidden="true" />
                        <span style={{ fontSize: 14 }}>{t('kds_loading_orders')}</span>
                    </div>
                ) : activeTab === 'active' ? (
                    visibleOrders.length === 0 ? (
                        <div className="kbs-center">
                            <div className="kbs-empty-art" style={{ opacity: categoryImageUrl ? 1 : 0.5 }}>
                                {categoryImageUrl
                                    ? renderEntityImage(categoryImageUrl, 56)
                                    : (category === 'Bar' ? '\u{1F379}' : '\u{1F468}\u{200D}\u{1F373}')}
                            </div>
                            <div className="kbs-empty-title">{t('kds_no_active_orders')}</div>
                            <div className="kbs-empty-sub">{t('kds_orders_appear_here')}</div>
                        </div>
                    ) : isMobile ? (
                        <>
                            <div className="kbs-carousel" ref={carouselRef} onScroll={handleCarouselScroll}>
                                {Array.from({ length: carouselPages }, (_, pageIdx) => (
                                    <div key={pageIdx} className="kbs-page">
                                        {visibleOrders.slice(pageIdx * 2, pageIdx * 2 + 2).map(order => (
                                            <TicketCard
                                                key={order.tracking_id}
                                                order={order}
                                                thresholds={thresholds}
                                                isNew={newOrderIds.has(order.tracking_id)}
                                                isCompleting={!!completingOrders[order.tracking_id]}
                                                onDone={() => handleDone(order.tracking_id)}
                                                onUndo={() => handleUndo(order.tracking_id)}
                                                language={language}
                                                t={t}
                                                compact
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                            {carouselPages > 1 && (
                                <div className="kbs-dots">
                                    {Array.from({ length: carouselPages }, (_, i) => (
                                        <span key={i} className={`kbs-dot${i === activeCarouselPage ? ' on' : ''}`} />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="kbs-grid" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
                            {visibleOrders.map(order => (
                                <TicketCard
                                    key={order.tracking_id}
                                    order={order}
                                    thresholds={thresholds}
                                    isNew={newOrderIds.has(order.tracking_id)}
                                    isCompleting={!!completingOrders[order.tracking_id]}
                                    onDone={() => handleDone(order.tracking_id)}
                                    onUndo={() => handleUndo(order.tracking_id)}
                                    language={language}
                                    t={t}
                                    compact={gridCols >= 5}
                                    tight={compactChrome}
                                />
                            ))}
                        </div>
                    )
                ) : activeTab === 'completed' ? (
                    <CompletedTab
                        orders={completedOrders}
                        thresholds={thresholds}
                        t={t}
                        language={language}
                        windowWidth={windowWidth}
                    />
                ) : (
                    <VoidedTab
                        voidedOrders={voidedOrdersList}
                        t={t}
                        language={language}
                        windowWidth={windowWidth}
                    />
                )}
            </div>
        </div>
    );
}
